import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { DraggableComponents } from '../../../../common/types/checkout';
import { BsAlarmFill } from "react-icons/bs";

const _Timer = styled.div<{ background?: string; color?: string }>`
  background: ${({ background }) => background || '#111'};
  color: ${({ color }) => color || 'white'};
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;

  @media (max-width: 640px) {
    gap: 8px;
    padding: 10px;
  }

  h3 {
    font-size: 24px;
    font-weight: bold;
    margin: 0;
    flex-shrink: 0;

    @media (max-width: 400px) {
      font-size: 20px;
    }
  }

  span {
    font-size: 14px;
    overflow: hidden;
    text-overflow: ellipsis;

    @media (max-width: 400px) {
      font-size: 12px;
    }
  }
`;

const IconWrapper = styled.div<{ size?: string }>`
  width: ${(props) => props.size || '24px'};
  height: ${(props) => props.size || '24px'};
  display: flex;
  align-items: center;
  justify-content: center;
`;

type Props = {
  component: DraggableComponents;
};

export const Timer: React.FC<Props> = ({ component }) => {
  const intervalRef = useRef<number | null>(null);
  const [timeLeft, setTimeLeft] = useState(300);

  const formatTime = (totalSeconds: number) => {
    const secs = Math.max(0, Math.floor(totalSeconds));
    const mins = Math.floor(secs / 60);
    const rem = secs % 60;
    return `${mins.toString().padStart(2, '0')}:${rem
      .toString()
      .padStart(2, '0')}`;
  };

  const clearTick = () => {
    if (intervalRef.current != null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const startTick = (initialSeconds: number) => {
    setTimeLeft(initialSeconds);
    clearTick();
    intervalRef.current = window.setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
  };

  useEffect(() => {
    const raw = Number(component?.configurations?.timer?.time ?? 5);
    const seconds = Number.isFinite(raw)
      ? Math.max(0, Math.round(raw * 60))
      : 300;
    startTick(seconds);

    return () => {
      clearTick();
    };
  }, [component?.configurations?.timer?.time]);

  useEffect(() => {
    return () => clearTick();
  }, []);

  return (
    <_Timer
      background={component?.configurations?.timer?.background}
      color={component?.configurations?.timer?.textColor}
    >
      <h3>{formatTime(timeLeft)}</h3>
      <IconWrapper size="40px">
        <BsAlarmFill size={25} />
      </IconWrapper>
      <span>{component?.configurations?.timer?.text}</span>
    </_Timer>
  );
};
